import { Form, Modal, ModalBody } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FaExclamationCircle } from "react-icons/fa";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInput } from "components/CustomInput";
import { Required } from "components/Required";
import { PrimaryButton } from "components/Button/style";
import { Informacao } from "components/Informacao";
import { createGlobalStyle } from "styled-components";
import { formatarDinheiro } from "_utils/Money";
import { useMaskInput } from "hooks/useMaskInput";
import { adicionarCustoGPSchema } from "../../../validate/adicionarCustoGPSchema";
import { IOptionsDescricaoGP } from "../../../hooks/useCriarLote";
import { ContainerForm, ContainerInstrucoes } from "./style";

const BackdropZIndexStyle = createGlobalStyle`
  .modal-backdrop.modal-adicionar-custo-backdrop {
    z-index: 1074;
  }
`;

interface IAdicionarCustoGP {
  idDescricao: number;
  valor: string;
  observacao: string | null;
}

interface IProps {
  show: boolean;
  handleClose: () => void;
  optionsDescricao: IOptionsDescricaoGP[];
  idFornecedor: number;
  carregarDescricoes: (idFornecedor?: number) => void;
  onSubmit: (dados: { idDescricao: number; label: string; valor: string; observacao: string }) => void;
}

export function ModalAdicionarCusto({
  show,
  handleClose,
  optionsDescricao,
  idFornecedor,
  carregarDescricoes,
  onSubmit,
}: IProps) {
  const { mInputValorBR } = useMaskInput();
  const [valorPreenchido, setValorPreenchido] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IAdicionarCustoGP>({
    resolver: yupResolver(adicionarCustoGPSchema),
  });

  useEffect(() => {
    if (show) {
      carregarDescricoes(idFornecedor > 0 ? idFornecedor : undefined);
      reset({ idDescricao: 0, valor: "", observacao: "" });
      setValorPreenchido("");
    }
  }, [show]);

  const onChangeDescricao = (id: number) => {
    if (!id) {
      setValue("valor", "");
      setValorPreenchido("");
      return;
    }
    const descricao = optionsDescricao.find((o) => o.value === id);
    if (descricao?.valorServico) {
      setValue("valor", descricao.valorServico, { shouldValidate: true });
      setValorPreenchido(descricao.valorServico);
    } else {
      setValue("valor", "");
      setValorPreenchido("");
    }
  };

  const onFormSubmit = (dados: IAdicionarCustoGP) => {
    const descricao = optionsDescricao.find((o) => o.value === dados.idDescricao);
    onSubmit({
      idDescricao: dados.idDescricao,
      label: descricao?.label ?? "",
      valor: dados.valor,
      observacao: dados.observacao ?? "",
    });
  };

  return (
    <>
      <BackdropZIndexStyle />
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard
        centered
        style={{ zIndex: 1075 }}
        backdropClassName="modal-adicionar-custo-backdrop"
      >
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}>
        <Modal.Title style={{ fontSize: "1.1rem", fontWeight: "bold" }}>Adicionar custo</Modal.Title>
        <span style={{ cursor: "pointer" }} onClick={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </span>
      </Modal.Header>
      <ModalBody style={{ padding: "20px" }}>
        <ContainerInstrucoes>
          Preencha as informações do custo que deseja adicionar aos processos selecionados.
        </ContainerInstrucoes>
        <Informacao
          mensagem={
            <span style={{ fontSize: "0.85rem" }}>
              Só é possível adicionar serviços financeiros previamente cadastrados para o
              fornecedor selecionado na criação do lote.
            </span>
          }
          type="atencao"
          showBotaoFechar={false}
          showLabel={false}
          icone={<FaExclamationCircle />}
          style={{ alignItems: "flex-start" }}
        />
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <ContainerForm>
            <Form.Group>
              <Form.Label>
                Custo <Required />
              </Form.Label>
              <Controller
                control={control}
                name="idDescricao"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={optionsDescricao}
                    selectedValue={value}
                    onSelect={(v) => {
                      onChange(v);
                      onChangeDescricao(Number(v));
                    }}
                    searchable
                    title=""
                    mensagemErro={errors.idDescricao?.message}
                  />
                )}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>
                Valor <Required />
              </Form.Label>
              <Controller
                control={control}
                name="valor"
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    placeholder="R$ 0,00"
                    style={{ height: "2.2rem" }}
                    value={
                      value != null && value !== ""
                        ? formatarDinheiro(Number(value))
                        : ""
                    }
                    onChange={(e: any) => {
                      const raw = mInputValorBR(e);
                      onChange(raw);
                      setValorPreenchido(raw);
                    }}
                    mensagemErro={errors.valor?.message}
                    isInvalid={!!errors.valor}
                  />
                )}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>
                Observação{" "}
                <small style={{ color: "#888", fontSize: "0.75rem" }}>
                  (será adicionada a todos os processos selecionados)
                </small>
              </Form.Label>
              <Controller
                control={control}
                name="observacao"
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    as="textarea"
                    rows={2}
                    placeholder="Insira uma observação"
                    style={{ resize: "none" }}
                    value={value ?? ""}
                    onChange={onChange}
                  />
                )}
              />
            </Form.Group>
          </ContainerForm>

          <div className="d-flex justify-content-end gap-3 mt-4">
            <PrimaryButton
              outline
              variante="primary"
              style={{ width: "9rem" }}
              type="button"
              onClick={handleClose}
            >
              Cancelar
            </PrimaryButton>
            <PrimaryButton
              style={{ width: "9rem" }}
              type="submit"
              disabled={!valorPreenchido || valorPreenchido === "0.00"}
            >
              Incluir
            </PrimaryButton>
          </div>
        </form>
      </ModalBody>
      </Modal>
    </>
  );
}
